import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CalendarIcon,
  CurecallCheckbox,
  CurecallDocumentViewer,
  CurecallModalContainer,
  CurecallTag,
  CurecallTooltip,
  DownloadIcon,
  ECurecallNotificationAppearance,
  EyeIcon,
  DialogueBubbleIcon,
  GenderFemaleIcon,
  GenderMaleIcon,
  LoadingSpinner,
} from '@honestica/curecall-ui'
import { formatPhone } from 'src/utils/phone.util'
import { EPatientGender, TPatient } from 'src/types/patient'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { TNavigationLocales } from 'src/types/locales'
import { twMerge } from 'src/utils/tw-merge.util'
import { useCurrentPatientFile } from 'src/pages/patients/patient-file/_components/patient-file.provider'
import { useNotification } from 'src/providers/notification.provider'
import { ProjectService } from 'src/services/project.service'
import { downloadBlob, getFileExtension } from 'src/utils/file-download.util'
import { getPatientFollowupDocumentFile } from './patient-forms/get-patient-followup-document-file'
import { usePatientFollowupDocumentFile } from './patient-forms/use-patient-followup-document-file.hook'
import {
  EPatientFollowupDocumentStatus,
  TPatientFollowupDocument,
  usePatientFollowupDocuments,
} from './patient-forms/use-patient-followup-documents.hook'
import { PDF_WORKER_SRC } from 'src/utils/pdf-worker.util'
import { isNotFoundError } from 'src/utils/http-error.util'
import { MissingDocumentFileBadge } from 'src/components/patient/missing-document-file-badge.component'

interface Props {
  patientFollowupId: number
  onToggle: VoidFunction
}

const getDocumentLabel = (document: TPatientFollowupDocument, locale: TNavigationLocales) =>
  getTranslatedLabel(locale, document.variableIntlLabel ?? undefined) || document.variableName

interface DocumentRowViewProps {
  row: TPatientFollowupDocument
  patientId: string
  projectId: string
}

const DocumentRowView = ({ row, patientId, projectId }: DocumentRowViewProps) => {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const { objectUrl, mimeType, isLoading, isError, error, refetch } = usePatientFollowupDocumentFile(
    row,
    patientId,
    projectId,
  )
  const isFileMissing = isError && isNotFoundError(error)

  useEffect(() => {
    if (isError && !isFileMissing) {
      notify({
        title: t('common.actions.error'),
        message: t('components.patient-documents-modal.view-error'),
        appearance: ECurecallNotificationAppearance.WARNING,
      })
    }
  }, [isError])

  if (isFileMissing) {
    return <MissingDocumentFileBadge />
  }

  return (
    <CurecallDocumentViewer
      objectUrl={objectUrl ?? ''}
      mimeType={mimeType}
      pdfWorkerSrc={PDF_WORKER_SRC}
      triggerIcon={<EyeIcon className="c-text-primary-600 c-transition-opacity hover:c-text-primary-700" />}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      variant="minimal"
      openLabel={t('components.patient-documents-modal.visualize')}
      lightboxLabels={{
        closeLabel: t('common.actions.close'),
        zoomInLabel: t('components.image-lightbox.zoom-in'),
        zoomOutLabel: t('components.image-lightbox.zoom-out'),
        zoomTo100Label: t('components.image-lightbox.zoom-to-100'),
        resetViewLabel: t('components.image-lightbox.reset-view'),
        imageViewerLabel: t('components.image-lightbox.image-viewer'),
        downloadLabel: t('common.actions.download'),
      }}
      pdfLabels={{
        pageLabel: (current, total) => t('components.pdf-viewer.page-label', { current, total }),
        previousPageLabel: t('components.pdf-viewer.previous-page'),
        nextPageLabel: t('components.pdf-viewer.next-page'),
        zoomInLabel: t('components.image-lightbox.zoom-in'),
        zoomOutLabel: t('components.image-lightbox.zoom-out'),
        downloadLabel: t('common.actions.download'),
        loadErrorLabel: t('components.pdf-viewer.load-error'),
        retryLabel: t('components.sms.image-retry'),
        loadingLabel: t('components.pdf-viewer.loading'),
        zoomTo100Label: t('components.image-lightbox.zoom-to-100'),
        resetScaleLabel: t('components.image-lightbox.reset-view'),
      }}
    />
  )
}

interface DocumentRowDownloadProps {
  row: TPatientFollowupDocument
  patientId: string
  projectId: string
}

const DocumentRowDownload = ({ row, patientId, projectId }: DocumentRowDownloadProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales
  const { notify } = useNotification()
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const response = await getPatientFollowupDocumentFile(row, patientId, projectId)
      const contentType = response.headers?.['content-type'] as string | undefined
      downloadBlob(
        new Blob([response.data as BlobPart], { type: contentType }),
        `${getDocumentLabel(row, locale)}.${getFileExtension(contentType)}`,
      )
    } catch {
      notify({
        title: t('common.actions.error'),
        message: t('components.patient-documents-modal.download-error'),
        appearance: ECurecallNotificationAppearance.WARNING,
      })
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <button
      onClick={handleDownload}
      disabled={isDownloading}
      aria-label={t('components.patient-documents-modal.column-download')}
      className={twMerge(
        'c-text-primary-600 c-transition-opacity',
        isDownloading && 'c-opacity-50 c-cursor-not-allowed',
        !isDownloading && 'hover:c-text-primary-700',
      )}
    >
      <DownloadIcon className="c-w-5 c-h-5" />
    </button>
  )
}

interface StatusBadgeProps {
  status: EPatientFollowupDocumentStatus
  requestedAt: string | null
  patient: Pick<TPatient, 'gender' | 'firstname' | 'lastname' | 'phone'>
}

const StatusBadge = ({ status, requestedAt, patient }: StatusBadgeProps) => {
  const { t } = useTranslation()

  if (status === EPatientFollowupDocumentStatus.RECEIVED) {
    return (
      <CurecallTag status="SUCCESS">{t('components.patient-documents-modal.status-available')}</CurecallTag>
    )
  }

  if (status === EPatientFollowupDocumentStatus.WAIVED) {
    return <CurecallTag isDisabled>{t('components.patient-documents-modal.status-waived')}</CurecallTag>
  }

  if (status === EPatientFollowupDocumentStatus.EXPECTED) {
    return (
      <CurecallTag status="WARNING">{t('components.patient-documents-modal.status-expected')}</CurecallTag>
    )
  }

  const date = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(requestedAt as string))
  const time = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(requestedAt as string))

  const GenderIcon =
    patient.gender === EPatientGender.MALE
      ? GenderMaleIcon
      : patient.gender === EPatientGender.FEMALE
        ? GenderFemaleIcon
        : null

  const civility =
    patient.gender === EPatientGender.MALE
      ? t('components.patient-documents-modal.civility-male')
      : patient.gender === EPatientGender.FEMALE
        ? t('components.patient-documents-modal.civility-female')
        : ''
  const fullName = [civility, patient.firstname, patient.lastname].filter(Boolean).join(' ')

  const tooltipContent = (
    <div className="c-flex c-flex-col c-gap-1.5 c-text-sm">
      <span className="c-flex c-items-start c-gap-1.5">
        <CalendarIcon className="c-w-3.5 c-h-3.5 c-shrink-0 c-mt-0.5" />
        {t('components.patient-documents-modal.tooltip-requested-on', { date, time })}
      </span>
      <span className="c-flex c-items-start c-gap-1.5">
        {GenderIcon ? (
          <GenderIcon className="c-w-3.5 c-h-3.5 c-shrink-0 c-mt-0.5" />
        ) : (
          <span className="c-w-3.5 c-h-3.5 c-shrink-0" />
        )}
        {fullName}
      </span>
      {patient.phone && (
        <span className="c-flex c-items-start c-gap-1.5">
          <DialogueBubbleIcon className="c-w-3.5 c-h-3.5 c-shrink-0 c-mt-0.5" />
          {formatPhone(patient.phone) || patient.phone}
        </span>
      )}
      <span>{t('components.patient-documents-modal.tooltip-awaiting-message')}</span>
    </div>
  )

  return (
    <CurecallTooltip content={tooltipContent} tooltipPosition="right">
      <CurecallTag status="INFORMATION">{t('components.patient-documents-modal.status-pending')}</CurecallTag>
    </CurecallTooltip>
  )
}

interface WaiveCheckboxProps {
  row: TPatientFollowupDocument
  patientFollowupId: number
  patientId: string
  projectId: string
  onWaived: VoidFunction
}

const WaiveCheckbox = ({ row, patientFollowupId, patientId, projectId, onWaived }: WaiveCheckboxProps) => {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const [isSaving, setIsSaving] = useState(false)
  const isReceived = row.status === EPatientFollowupDocumentStatus.RECEIVED

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIsWaived = event.target.checked
    setIsSaving(true)
    try {
      await ProjectService.setDocumentWaived(
        patientFollowupId,
        row.variableId,
        newIsWaived,
        patientId,
        projectId,
      )
      onWaived()
    } catch {
      notify({
        title: t('common.actions.error'),
        message: t('components.patient-documents-modal.waive-error'),
        appearance: ECurecallNotificationAppearance.WARNING,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="c-flex c-justify-center">
      <CurecallCheckbox
        checked={row.status === EPatientFollowupDocumentStatus.WAIVED || isReceived}
        onChange={handleChange}
        isDisabled={isSaving || isReceived}
        aria-label={t('components.patient-documents-modal.waive-checkbox-label')}
      />
    </div>
  )
}

const PatientDocumentsModal = ({ patientFollowupId, onToggle }: Props) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales
  const { projectId, patientId } = useParams()
  const { patient } = useCurrentPatientFile()
  const {
    documents,
    isLoading,
    isError,
    refetch: refetchDocuments,
  } = usePatientFollowupDocuments(patientFollowupId, patientId as string, projectId as string)

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('common.actions.close')}
      title={t('components.patient-documents-modal.title')}
      isOpen={true}
      toggle={onToggle}
      dialogClassName="sm:c-w-[800px]"
    >
      <p className="c-text-sm c-text-gray-500 c-mb-4">{t('components.patient-documents-modal.subtitle')}</p>
      {isError ? (
        <div className="c-flex c-items-center c-gap-3 c-rounded c-border c-border-red-200 c-bg-red-50 c-px-4 c-py-3 c-mb-4 c-text-sm c-text-red-700">
          <span>{t('components.patient-documents-modal.load-error')}</span>
          <button onClick={() => refetchDocuments()} className="c-underline c-font-medium">
            {t('components.patient-documents-modal.retry')}
          </button>
        </div>
      ) : isLoading ? (
        <div className="c-flex c-justify-center c-py-8">
          <LoadingSpinner />
        </div>
      ) : documents.length === 0 ? (
        <p className="c-py-8 c-text-center c-text-muted">
          {t('components.patient-documents-modal.no-documents')}
        </p>
      ) : (
        <div className="c-flow-root c--mx-6 c-overflow-x-auto c-min-w-full c-align-middle c-px-6">
          <table className="c-min-w-full c-divide-y c-divide-gray-200">
            <thead className="c-bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="c-py-3 c-pr-3 c-text-left c-text-sm c-font-semibold c-text-gray-500 c-pl-0"
                >
                  {t('components.patient-documents-modal.column-document')}
                </th>
                <th
                  scope="col"
                  className="c-py-3 c-px-3 c-text-left c-text-sm c-font-semibold c-text-gray-500"
                >
                  {t('components.patient-documents-modal.column-status')}
                </th>
                <th
                  scope="col"
                  className="c-py-3 c-px-3 c-text-center c-text-sm c-font-semibold c-text-gray-500"
                >
                  {t('components.patient-documents-modal.column-view')}
                </th>
                <th
                  scope="col"
                  className="c-py-3 c-px-3 c-text-center c-text-sm c-font-semibold c-text-gray-500"
                >
                  {t('components.patient-documents-modal.column-download')}
                </th>
                <th
                  scope="col"
                  className="c-py-3 c-px-3 c-text-center c-text-sm c-font-semibold c-text-gray-500"
                >
                  {t('components.patient-documents-modal.column-waive')}
                </th>
              </tr>
            </thead>
            <tbody className="c-divide-y c-divide-gray-200">
              {documents.map((row) => (
                <tr key={row.variableId}>
                  <td className="c-py-4 c-pr-3 c-text-sm c-pl-0">{getDocumentLabel(row, locale)}</td>
                  <td className="c-py-4 c-px-3 c-text-sm">
                    <StatusBadge status={row.status} requestedAt={row.requestedAt} patient={patient} />
                  </td>
                  <td className="c-py-4 c-px-3 c-text-center">
                    {row.status === EPatientFollowupDocumentStatus.RECEIVED ? (
                      <DocumentRowView
                        row={row}
                        patientId={patientId as string}
                        projectId={projectId as string}
                      />
                    ) : (
                      <CurecallTooltip
                        content={t('components.patient-documents-modal.no-file-tooltip')}
                        tooltipPosition="top"
                      >
                        <EyeIcon
                          aria-hidden="true"
                          className="c-mx-auto c-text-gray-300 c-cursor-not-allowed"
                        />
                      </CurecallTooltip>
                    )}
                  </td>
                  <td className="c-py-4 c-px-3 c-text-center">
                    {row.status === EPatientFollowupDocumentStatus.RECEIVED ? (
                      <DocumentRowDownload
                        row={row}
                        patientId={patientId as string}
                        projectId={projectId as string}
                      />
                    ) : (
                      <CurecallTooltip
                        content={t('components.patient-documents-modal.no-file-tooltip')}
                        tooltipPosition="top-end"
                      >
                        <DownloadIcon
                          aria-hidden="true"
                          className="c-w-5 c-h-5 c-mx-auto c-text-gray-300 c-cursor-not-allowed"
                        />
                      </CurecallTooltip>
                    )}
                  </td>
                  <td className="c-py-4 c-px-3 c-text-center">
                    <WaiveCheckbox
                      row={row}
                      patientFollowupId={patientFollowupId}
                      patientId={patientId as string}
                      projectId={projectId as string}
                      onWaived={() => refetchDocuments()}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CurecallModalContainer>
  )
}

export { PatientDocumentsModal }
